import { Action, AgentRunEvent, Document } from '@lucidtech/las-sdk-browser';
import { useMemo } from 'react';

import {
  CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID,
  EMAIL_CONTENT_TYPE,
  EXPORT_FUNCTION_IDS,
  REFETCH_AGENT_RUN_INTERVAL,
  TRIGGER_FUNCTION_IDS,
} from '@/constants';
import {
  toActionId,
  toAgentId,
  toAgentRunId,
  toDocumentId,
  useBatchGetActions,
  useBatchGetDocument,
  useGetAgent,
  useGetAgentRun,
} from '@/hooks/api';
import { isActionId, isDocumentId, isPredictionId, isUserId, isValidationId } from '@/utils';

const isPredictionEvent = (event: AgentRunEvent) => {
  return isPredictionId(event.resourceId);
};

const isReviewEvent = (event: AgentRunEvent) => {
  return isValidationId(event.validationId);
};

const isExportEvent = (event: AgentRunEvent, actions: Action[]) => {
  const action = actions.find((a) => a.actionId === event.actionId);
  return action ? EXPORT_FUNCTION_IDS.includes(action.functionId) : false;
};

const mergeEvents = (events: AgentRunEvent[], currentEvent: AgentRunEvent) => {
  const exists = !!events.find((e) => e.resourceId === currentEvent.resourceId);
  if (!exists) events.push(currentEvent);
  return events;
};

const createTriggerEvents = (
  taskDocument: Document | undefined,
  emailDocument: Document | undefined,
  actions: Action[]
) => {
  if (!taskDocument) return [];

  const event: AgentRunEvent = {
    resourceId: taskDocument.documentId,
    timestamp: taskDocument.createdTime,
    status: 'succeeded',
  };

  if (emailDocument) {
    event.actionId = actions.find((a) => a.functionId === CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID)?.actionId;
  } else if (isUserId(taskDocument.createdBy)) {
    event.trigger = 'manual';
    event.userId = taskDocument.createdBy;
  } else {
    const triggerActions = actions.filter(
      (a) => a.functionId !== CREATE_AGENT_RUN_FROM_EMAIL_FUNCTION_ID && TRIGGER_FUNCTION_IDS.includes(a.functionId)
    );
    if (triggerActions.length === 1) {
      event.actionId = triggerActions[0].actionId;
    } else {
      event.trigger = 'api';
    }
  }

  return [event];
};

export type UseAgentRunTreeOpts = {
  agentId?: string | null;
  runId?: string | null;
};

export const useAgentRunTree = ({ agentId, runId }: UseAgentRunTreeOpts) => {
  const { data: agent } = useGetAgent(toAgentId(agentId));
  const { data: run } = useGetAgentRun(toAgentRunId(agentId, runId), {
    fetchFromListCache: true,
    refetchInterval: REFETCH_AGENT_RUN_INTERVAL,
  });
  const documentIds = useMemo(() => run?.resourceIds.filter(isDocumentId).map(toDocumentId), [run]);
  const { data: documents, isPending: isDocumentsPending } = useBatchGetDocument(documentIds);
  const actionIds = useMemo(() => agent?.resourceIds.filter(isActionId).map(toActionId), [agent]);
  const { data: actions } = useBatchGetActions(actionIds);
  const emailDocument = useMemo(() => documents.find((d) => d.contentType === EMAIL_CONTENT_TYPE), [documents]);
  const taskDocument = useMemo(() => documents.find((d) => d.contentType !== EMAIL_CONTENT_TYPE), [documents]);

  const isDocumentsDeleted = useMemo(
    () => documentIds && !isDocumentsPending && documents.length !== documentIds.length,
    [documentIds, isDocumentsPending, documents.length]
  );

  const { triggerEvents, predictionEvents, reviewEvents, exportEvents } = useMemo(() => {
    let triggerEvents;
    if (isDocumentsDeleted) {
      triggerEvents = [
        {
          resourceId: 'unknown',
          timestamp: new Date(),
          status: 'failed',
          trigger: 'unknown',
        },
      ] as AgentRunEvent[];
    } else {
      triggerEvents = createTriggerEvents(taskDocument, emailDocument, actions);
    }
    let predictionEvents = [];
    let reviewEvents = [];
    let exportEvents = [];

    for (const event of (run?.events ?? []).toSorted((a, b) =>
      a.timestamp < b.timestamp ? -1 : a.timestamp > b.timestamp ? 1 : 0
    )) {
      if (isPredictionEvent(event)) predictionEvents.push(event);
      if (isReviewEvent(event)) reviewEvents.push(event);
      if (isExportEvent(event, actions)) exportEvents.push(event);
    }

    triggerEvents = triggerEvents.reduceRight(mergeEvents, []);
    predictionEvents = predictionEvents.reduceRight(mergeEvents, []);
    reviewEvents = reviewEvents.reduceRight(mergeEvents, []);
    exportEvents = exportEvents.reduceRight(mergeEvents, []);

    triggerEvents.reverse();
    predictionEvents.reverse();
    reviewEvents.reverse();
    exportEvents.reverse();

    return { triggerEvents, predictionEvents, reviewEvents, exportEvents };
  }, [taskDocument, emailDocument, actions, run?.events, isDocumentsDeleted]);

  return {
    emailDocument,
    taskDocument,
    triggerEvents,
    predictionEvents,
    reviewEvents,
    exportEvents,
    isDocumentsDeleted,
  };
};
